if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomOutBtn = this.querySelector('#modal-zoom-out');
        this.zoomInBtn = this.querySelector('#modal-zoom-in');
        this.currentScale = 1;
        this.posX = 0;
        this.posY = 0;

        if (this.zoomInBtn && this.zoomOutBtn) {
          this.zoomInBtn.addEventListener('click', () => this.zoom(1));
          this.zoomOutBtn.addEventListener('click', () => this.zoom(-1));
        }

        const postIframeCommand = (iframe, message) => {
          if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(JSON.stringify(message), '*');
          }
        };

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target.querySelector('deferred-media');
              if (!deferredMedia) return;

              if (entry.isIntersecting) {
                if (prefersReducedMotion) return;
                if (entry.target.querySelector('[data-video-toggle-container][data-user-paused]')) return;

                if (!deferredMedia.getAttribute('loaded')) {
                  deferredMedia.loadContent(false);
                }
                const poster = deferredMedia.querySelector('.deferred-media__poster');
                if (poster) poster.style.display = 'none';
                const video = deferredMedia.querySelector('video');
                if (video) {
                  video.muted = true;
                  requestAnimationFrame(() => video.play().catch(() => {}));
                }
              } else if (deferredMedia.getAttribute('loaded')) {
                const video = deferredMedia.querySelector('video');
                if (video) video.pause();
                postIframeCommand(deferredMedia.querySelector('.js-youtube'), { event: 'command', func: 'pauseVideo', args: '' });
                postIframeCommand(deferredMedia.querySelector('.js-vimeo'), { method: 'pause' });
              }
            });
          },
          { threshold: 0.3, root: this.querySelector('[role="document"]') }
        );
      }

      hide() {
        super.hide();
        this.resetZoom();
        this.videoObserver.disconnect();
        this.querySelectorAll('deferred-media[loaded] video').forEach((v) => v.pause());

        const slider = this.querySelector('slider-component');
        if (slider && this._slideChangedListener) {
          slider.removeEventListener('slideChanged', this._slideChangedListener);
          this._slideChangedListener = null;
        }
      }

      loadDeferredImages() {
        this.querySelectorAll('img[data-src]').forEach((image) => {
          const srcset = image.getAttribute('data-srcset');
          if (srcset) {
            image.setAttribute('srcset', srcset);
            image.removeAttribute('data-srcset');
          }
          image.setAttribute('src', image.getAttribute('data-src'));
          image.removeAttribute('data-src');
        });
      }

      show(opener) {
        this.loadDeferredImages();
        super.show(opener);
        this.showActiveMedia();
        this.querySelectorAll('.product-modal__slide').forEach((slide) => {
          const dm = slide.querySelector('deferred-media');
          if (dm) this.videoObserver.observe(slide);
        });

        this.updateSlideInert();
        const slider = this.querySelector('slider-component');
        if (slider && !this._slideChangedListener) {
          this._slideChangedListener = () => this.updateSlideInert();
          slider.addEventListener('slideChanged', this._slideChangedListener);
        }
      }

      updateSlideInert() {
        this.querySelectorAll('.product-modal__slide').forEach((slide) => {
          if (slide.classList.contains('active')) {
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('inert', '');
          }
        });
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent) {
          activeMedia.loadContent();
        }

        // Reset zoom when showing a new active slide
        this.resetZoom();

      }

      zoom(direction) {
        const activeSlide = this.querySelector('.product-modal__slide.active img');
        if (!activeSlide) return;

        // Adjust scale, keeping within 1–4 range
        this.currentScale = Math.max(1, Math.min(4, this.currentScale + direction));

        activeSlide.style.transform = `translate3d(${this.posX}px, ${this.posY}px, 0px) scale3d(${this.currentScale}, ${this.currentScale}, 1)`;

        if (this.currentScale === 1) {
          activeSlide.classList.remove('is-zoomed');
        } else {
          activeSlide.classList.add('is-zoomed');
        }

        // Dispatch a custom event with current scale
        this.dispatchEvent(new CustomEvent('scalechange', {
          detail: { scale: this.currentScale },
          bubbles: true,
          composed: true
        }));
      }

      resetZoom() {
        this.currentScale = 1;
        const allImages = this.querySelectorAll('.product-modal__slide img');
        allImages.forEach(img => {
          img.style.transform = `scale(${this.currentScale})`;
        });
      }
    }
  );
}

if (!customElements.get('image-zoom')) {
  customElements.define(
    'image-zoom',
    class ImageZoom extends HTMLElement {
      constructor() {
        super();
        this.img = this.querySelector('img');
        this.scale = 1;
        this.start = {};
        this.posX = 0;
        this.posY = 0;

        // Listen for scale changes from product-modal or other sources
        this.addEventListener('scalechange', (event) => {
          this.scale = event.detail.scale;
          this.updateTransform();
        });

        if (this.img) {
          this.img.addEventListener('wheel', (event) => this.onWheel(event));
          this.img.addEventListener('mousedown', (event) => this.onMouseDown(event));
          this.img.addEventListener('mousemove', (event) => this.onMouseMove(event));
          this.img.addEventListener('mouseup', () => this.onMouseUp());
          this.img.addEventListener('mouseleave', () => this.onMouseLeave());
          this.pinchZoom();
        }
      }

      onWheel(event) {
        event.preventDefault();
        const delta = Math.sign(event.deltaY);
        this.scale = Math.max(1, Math.min(4, this.scale - delta * 0.1));
        this.updateTransform();
      }

      onMouseDown(event) {
        event.preventDefault();
        this.isDragging = true;
        this.startX = event.pageX - this.posX;
        this.startY = event.pageY - this.posY;
      }

      onMouseMove(event) {
        if (!this.isDragging) return;
        event.preventDefault();
        const x = event.pageX - this.startX;
        const y = event.pageY - this.startY;
        this.posX = x;
        this.posY = y;
        this.updateTransform();
      }

      onMouseUp() {
        this.isDragging = false;
      }

      onMouseLeave() {
        this.isDragging = false;
      }

      pinchZoom() {
        const distanceBetweenTouchPoints = (event) => {
          return Math.hypot(
            event.touches[0].pageX - event.touches[1].pageX,
            event.touches[0].pageY - event.touches[1].pageY
          );
        }

        this.addEventListener('touchstart', (event) => {
          if (event.touches.length === 2) {
            event.preventDefault();

            this.start = {
              distance: distanceBetweenTouchPoints(event),
              x: (event.touches[0].pageX + event.touches[1].pageX) / 2,
              y: (event.touches[0].pageY + event.touches[1].pageY) / 2,
            };
          }
        });

        this.addEventListener('touchmove', (event) => {
          if (event.touches.length === 2) {
            event.preventDefault();

            if (event.scale) {
              this.scale = event.scale >= 1 ? event.scale : 1;
            } else {
              const currentDistance = distanceBetweenTouchPoints(event);
              this.scale = (currentDistance / this.start.distance) >= 1 ? (currentDistance / this.start.distance) : 1;
            }

            this.updateTransform();
          }
        });

        this.addEventListener('touchend', (event) => {
          this.img.style.transform = ""; // Reset transform to avoid sticky zoom
          this.removeEventListener('touchmove', this);
          this.removeEventListener('touchend', this);
        });
      }

      updateTransform() {
        if (this.img) {
          this.img.style.transform = `translate3d(${this.posX}px, ${this.posY}px, 0px) scale3d(${this.scale}, ${this.scale}, 1)`;
        }
      }
    }
  );
}
